import { lazy, Suspense, type ReactNode } from "react";
import { firstSectionPage, hasSectionAccess, sectionForPage, SECTION_CATALOG, type SectionKey } from "@vekha/contracts";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "./components/app-shell";
import { LoadingScreen, UnavailableScreen, Notice } from "./components/ui";
import { useApp } from "./lib/app-context";
import { WorkContextProvider } from "./lib/work-context";
import { useNativeCalendar } from "./lib/native-calendar-context";
import { legacyWebinarTarget } from "./lib/webinar-analytics-api";
import { loadAnalyticsPage, loadedAnalyticsPage } from "./lib/analytics-page-loader";
import { loadedDialogsPage } from "./lib/dialogs-page-loader";
import type { AnalyticsSectionKey } from "@vekha/contracts";

const CrmDealsPage = lazy(async () => ({
  default: (await import("./pages/crm-deals-page")).CrmDealsPage,
}));
/** «Диалоги» rendered directly when main.tsx has already loaded their code (no held-back reveal, see dialogs-page-loader). */
function DialogsPage() {
  const Loaded = loadedDialogsPage();
  return Loaded ? <Loaded mode="dialogs" /> : <CrmDealsPage mode="dialogs" />;
}
const AgentsPage = lazy(async () => ({ default: (await import("./pages/agents-page")).AgentsPage }));
const ScenariosPage = lazy(async () => ({ default: (await import("./pages/scenarios-page")).ScenariosPage }));
const AutoMessagesPage = lazy(async () => ({ default: (await import("./pages/auto-messages-page")).AutoMessagesPage }));
const ConnectionsPage = lazy(async () => ({ default: (await import("./pages/connections-page")).ConnectionsPage }));
const ActionLogPage = lazy(async () => ({ default: (await import("./pages/action-log-page")).ActionLogPage }));
const AccessPage = lazy(async () => ({ default: (await import("./pages/access-page")).AccessPage }));
const StatusPage = lazy(async () => ({
  default: (await import("./pages/status-page")).StatusPage,
}));
const CampaignsPage = lazy(async () => ({ default: (await import("./pages/campaigns")).CampaignsPage }));
const BotCampaignsPage = lazy(async () => ({ default: (await import("./pages/campaigns/bot-campaigns")).BotCampaignsPage }));
const BotCampaignAnalyticsPage = lazy(async () => ({ default: (await import("./pages/campaigns/bot-campaigns")).BotCampaignAnalyticsPage }));
const CampaignCreatePage = lazy(async () => ({ default: (await import("./pages/campaigns")).CampaignCreatePage }));
const CampaignPage = lazy(async () => ({ default: (await import("./pages/campaigns")).CampaignPage }));
const CampaignEditPage = lazy(async () => ({ default: (await import("./pages/campaigns")).CampaignEditPage }));
const CalendarPage = lazy(async () => ({ default: (await import("./pages/calendar-page")).CalendarPage }));
const CalendarAnalyticsPage=lazy(async()=>({default:(await import("./pages/calendar-analytics-page")).CalendarAnalyticsPage}));
const WebinarsPage = lazy(async () => ({ default: (await import("./pages/webinars-page")).WebinarsPage }));
const LeadMagnetsPage = lazy(async () => ({default:(await import("./pages/lead-magnets-page")).LeadMagnetsPage}));
const SitesPage = lazy(async () => ({default:(await import("./pages/sites-page")).SitesPage}));
const SiteFormPage = lazy(async () => ({default:(await import("./pages/site-forms-page")).SiteFormPage}));
const LeadMagnetPage = lazy(async () => ({default:(await import("./pages/lead-magnets-page")).LeadMagnetPage}));
const WebinarAnalyticsPage = lazy(async () => ({ default: (await import("./pages/webinar-analytics")).WebinarAnalyticsPage }));
const WebinarAnalyticsIndexPage = lazy(async () => ({ default: (await import("./pages/webinars-page")).WebinarAnalyticsIndexPage }));
const CampaignAnalyticsPage = lazy(async () => ({ default: (await import("./pages/campaigns")).CampaignAnalyticsPage }));
const LazyAnalyticsPage = lazy(async () => ({ default: await loadAnalyticsPage() }));
/** Rendered directly when its code is already here (main.tsx loads it with the page): no suspense, no held-back reveal. */
function AnalyticsPage({ section }: { section: AnalyticsSectionKey }) {
  const Loaded = loadedAnalyticsPage();
  return Loaded ? <Loaded section={section} /> : <LazyAnalyticsPage section={section} />;
}
const CampaignAnalyticsDetailPage = lazy(async () => ({ default: (await import("./pages/campaigns")).CampaignAnalyticsDetailPage }));

function SectionBoundary({ children }: { children: ReactNode }) {
  const { data, workspaceReadyFor } = useApp();
  const location = useLocation();
  const section = /^\/(?:webinars|calendar)\/(?:events|series)\//u.test(location.pathname) ? "analytics_webinars" : sectionForPage(location.pathname);
  const access = data.actor?.sectionAccess;
  if (section && !hasSectionAccess(access, section)) return <div className="page"><Notice tone="info" title="Доступ к разделу не выдан">Права меняет владелец или администратор Вехи.</Notice></div>;
  // «Диалоги» read only their part of the snapshot: the operator's other pages opened after them wait for the whole one.
  if (workspaceReadyFor && !workspaceReadyFor(location.pathname)) return <div className="route-loading" aria-label="Загрузка раздела" />;
  return <>{section && access && !hasSectionAccess(access, section, "edit") ? <div className="section-read-only">Доступ только к чтению</div> : null}{children}</>;
}

/** `/calendar/series/:key` and `/calendar/events/:id` moved to «Эфиры»; links to them live on in chats and notes. */
function MovedWebinarPage() {
  const location = useLocation();
  const target = location.pathname.startsWith("/webinars/") ? location.pathname.replace("/webinars/","/analytics/webinars/") + location.search : legacyWebinarTarget(location.pathname,location.search);
  return <Navigate to={target?.replace(/^\/webinars\/(events|series)\//u,"/analytics/webinars/$1/") ?? "/webinars"} replace />;
}

const WherePage = lazy(async () => ({ default: (await import("./pages/where-page")).WherePage }));
const RobotsPage = lazy(async () => ({ default: (await import("./pages/robots-page")).RobotsPage }));
const PromisesPage = lazy(async () => ({ default: (await import("./pages/promises-page")).PromisesPage }));
const AlertsPage = lazy(async () => ({ default: (await import("./pages/alerts-page")).AlertsPage }));
const BotsPage = lazy(async () => ({ default: (await import("./pages/bots-page")).BotsPage }));
const AgentConnectionsPage = lazy(async () => ({ default: (await import("./pages/agent-connections-page")).AgentConnectionsPage }));
const SemanticStagingPage = lazy(async () => ({ default: (await import("./pages/semantic-staging-page")).SemanticStagingPage }));

/** Exhaustive page binding: a new section cannot ship without its registered route. */
const sectionPages = {
  dialogs: <DialogsPage />, campaigns: <CampaignsPage />,
  analytics_summary: <AnalyticsPage section="summary" />, analytics_ads: <AnalyticsPage section="ads" />, analytics_practicum: <AnalyticsPage section="practicum" />, analytics_sales: <AnalyticsPage section="sales" />,
  analytics_lidorub: <AnalyticsPage section="lidorub" />, analytics_money: <AnalyticsPage section="money" />, analytics_entries: <AnalyticsPage section="entries" />,
  analytics_consultations: <AnalyticsPage section="consultations" />, analytics_voice: <AnalyticsPage section="voice" />,
  // The old «Аналитика» is a permission for all its sections now, not a page of its own.
  analytics: <Navigate to="/analytics" replace />,
  webinars: <WebinarsPage />, calendar: <CalendarPage />, lead_magnets: <LeadMagnetsPage />, site_forms: <SitesPage />,
  analytics_webinars: <WebinarAnalyticsIndexPage />, analytics_campaigns: <CampaignAnalyticsPage />,
  agents: <AgentsPage />, scenarios: <ScenariosPage />, auto_messages: <AutoMessagesPage />,
  playground: <SemanticStagingPage />, connections: <ConnectionsPage />, action_log: <ActionLogPage />,
  status: <StatusPage />, alerts: <AlertsPage />, promises: <PromisesPage />, robots: <RobotsPage />,
  bots: <BotsPage />, where: <WherePage />, access: <AccessPage />,
} satisfies Record<SectionKey, ReactNode>;

export function App() {
  const { bootstrapError, retryApplication, status, data } = useApp();
  const calendar = useNativeCalendar();
  if (status === "loading") return <LoadingScreen />;
  if (status === "unavailable") {
    return (
      <>
        <UnavailableScreen
          error={bootstrapError ?? "Сервер не подтвердил рабочее состояние."}
          onRetry={retryApplication}
        />
        <p className="recovery-entry"><Link to="/recovery">Восстановление доступа и базы</Link></p>
      </>
    );
  }

  return (
    <WorkContextProvider>
      <AppShell>
        <SectionBoundary><Suspense fallback={<div className="route-loading" aria-label="Загрузка раздела" />}>
          <Routes>
            {SECTION_CATALOG.filter(section => !section.calendarOnly || calendar.enabled).map(section => <Route key={section.key} path={section.path} element={sectionPages[section.key]} />)}
            <Route path="/lead-magnets/:magnetId" element={<LeadMagnetPage />} />
            <Route path="/site-forms/:formId" element={<SiteFormPage />} />
            {/* «Формы» became «Сайты» (17.10.2026): the old list link opens the new page; a form keeps its address. */}
            <Route path="/site-forms" element={<Navigate to="/sites" replace />} />
            <Route path="/campaigns/bots" element={<BotCampaignsPage />} />
            <Route path="/analytics/campaigns/bots" element={<BotCampaignAnalyticsPage />} />
            <Route path="/campaigns/new" element={<CampaignCreatePage />} />
            <Route path="/campaigns/:campaignId" element={<CampaignPage />} />
            <Route path="/campaigns/:campaignId/edit" element={<CampaignEditPage />} />
            <Route path="/analytics/webinars/events/:eventId" element={<WebinarAnalyticsPage />} />
            <Route path="/analytics/webinars/series/:seriesKey" element={<WebinarAnalyticsPage />} />
            <Route path="/analytics/campaigns/:campaignId" element={<CampaignAnalyticsDetailPage />} />
            {/* «Команда» was split: Юля went to «База Реги», the managers to «Продажи». Old links still open. */}
            <Route path="/analytics/team" element={<Navigate to="/analytics/base-regi" replace />} />
            {calendar.enabled?<Route path="/calendar/analytics" element={<CalendarAnalyticsPage/>}/>:null}
            {calendar.enabled ? <Route path="/webinars/events/:eventId" element={<MovedWebinarPage />} /> : null}
            {calendar.enabled ? <Route path="/webinars/series/:seriesKey" element={<MovedWebinarPage />} /> : null}
            {calendar.enabled ? <Route path="/calendar/events/:eventId" element={<MovedWebinarPage />} /> : null}
            {calendar.enabled ? <Route path="/calendar/series/:seriesKey" element={<MovedWebinarPage />} /> : null}
            <Route path="/ai-agents" element={<AgentConnectionsPage />} />
            <Route path="/leads" element={<Navigate to="/dialogs" replace />} />
            <Route path="/review" element={<Navigate to="/dialogs?view=needs_reply" replace />} />
            <Route path="/conversations" element={<Navigate to="/dialogs" replace />} />
            <Route path="/integrations" element={<Navigate to="/connections" replace />} />
            <Route path="/" element={<Navigate to={firstSectionPage(data.actor?.sectionAccess)} replace />} />
            <Route path="*" element={<Navigate to={firstSectionPage(data.actor?.sectionAccess)} replace />} />
          </Routes>
        </Suspense></SectionBoundary>
      </AppShell>
    </WorkContextProvider>
  );
}
